import React, { useState, useEffect } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import api from '../../services/api';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) setUser(JSON.parse(savedUser));
  }, []);

  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/;
    if (!passwordRegex.test(passwords.new)) {
      setMessage({ type: 'error', text: 'New password must be at least 8 characters and contain uppercase, lowercase, numbers, and special characters.' });
      return;
    }
    if (passwords.new !== passwords.confirm) {
      setMessage({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await api.post('/user/change-password', {
        email: user.email,
        current_password: passwords.current,
        new_password: passwords.new
      });
      setMessage({ type: 'success', text: 'Password updated successfully!' });
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to update password. Backend service unavailable.' });
    } finally {
      setLoading(false);
    }
  };

  if (!user) return <div>Loading...</div>;

  return (
    <div className="animate-fade-in" style={{ display: 'flex', flexWrap: 'wrap', gap: '32px' }}>
      
      <div className="card glass" style={{ alignSelf: 'start', flex: '1 1 300px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '20px 0' }}>
          <div style={{ 
            width: '120px', 
            height: '120px', 
            borderRadius: '50%', 
            background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.1), rgba(59, 130, 246, 0.1))', 
            border: '2px solid var(--border-color)',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            color: 'var(--primary)',
            marginBottom: '24px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <PersonIcon style={{ fontSize: '64px' }} />
          </div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>{user.name}</h2>
          <p style={{ color: 'var(--text-muted)', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e' }}></span>
            Active Session
          </p>
          
          <div style={{ width: '100%', borderTop: '1px solid var(--border-color)', margin: '24px 0', paddingTop: '24px', textAlign: 'left' }}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '12px', marginBottom: '4px' }}>Email Address</label>
              <div style={{ fontWeight: 500 }}>{user.email}</div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '12px', marginBottom: '4px' }}>Account Type</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                {(user.roles || []).map(role => (
                   <span key={role} style={{ fontSize: '11px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', padding: '4px 12px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {role.replace('_', ' ')}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card glass">
        <h3 style={{ marginBottom: '8px' }}>Security Settings</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '32px' }}>Update your password to keep your account secure.</p>
        
        {message.text && (
          <div style={{ 
            padding: '16px', 
            background: message.type === 'error' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(34, 197, 94, 0.1)', 
            color: message.type === 'error' ? '#ef4444' : '#22c55e', 
            borderRadius: '12px', 
            marginBottom: '32px', 
            fontSize: '14px',
            border: `1px solid ${message.type === 'error' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(34, 197, 94, 0.2)'}`
          }}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleChangePassword} style={{ maxWidth: '500px' }}>
          <div className="input-group">
            <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: 500 }}>Current Password</label>
            <input 
              type="password" 
              placeholder="Enter current password"
              value={passwords.current}
              onChange={(e) => setPasswords({...passwords, current: e.target.value})}
              required 
            />
          </div>
          <div className="input-group">
            <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: 500 }}>New Password</label>
            <input 
              type="password" 
              placeholder="Enter new password"
              value={passwords.new}
              onChange={(e) => setPasswords({...passwords, new: e.target.value})}
              required 
            />
          </div>
          <div className="input-group">
            <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: 500 }}>Confirm New Password</label>
            <input 
              type="password" 
              placeholder="Confirm new password"
              value={passwords.confirm}
              onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
              required 
            />
          </div>
          
          <button type="submit" className="btn btn-primary" style={{ height: '48px', padding: '0 32px', marginTop: '16px' }} disabled={loading}>
            {loading ? 'Updating Credentials...' : 'Update Security Credentials'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Profile;
